import { useMemo } from 'react'
import { useOrganisationDetails } from 'src/api/user/list'
import {
  GROUP_ANALYSES,
  WORKFLOW_DEFAULTS,
  aiAvailable,
  annotationTypeOf,
  cnvAvailable,
  effectiveLongRead,
  isTumourRna,
  libraryOf,
} from 'src/services/analysisStart'
import { effectiveAI } from './samples'

// ----------------------------------------------------------------------
// Credits for the New analysis panel. Every formula mirrors what the backend
// charges (libra_service GenericAnnotateView and the group views); the server sets
// the final charge, so these are estimates the user sees before a start.
//
// One start per sample, one charge per start:
//   VCF      vcf_price, plus ai_price with AI. No CNV or SV surcharge.
//   FASTQ    whole_genome_fastq_price (WGS), targeted_whole_exome_fastq_price (WES)
//            or targeted_clinical_exome_fastq_price (CES); plus cnv_price when CNV
//            runs (germline exome only), plus ai_price with AI. Tumour-only costs
//            the same; tumour-normal costs tumour_normal_*_fastq_price (the server
//            returns it resolved, 1.5x unless the organisation set its own), once
//            for both samples. Lanes of one sample are one charge.
//   RNA      rna_targeted_fastq_price for an exome-capture or targeted panel
//            library, else rna_fastq_price (whole transcriptome).
//   ONT      ont_bam_price, plus ai_price with AI. CNV, SV and methylation are free.
//   PacBio   pacbio_bam_price, plus ai_price with AI, plus cnv_price when its CNV
//            runs. SV and methylation are free.
// Group analyses:
//   Carrier      carrier_price per carrier analysis (the whole couple or group).
//   Multisample  free.
//   CNV cohort   free (cnv_cohort_price is recorded, never deducted).
//
// A start is refused (402) when its price is above the balance; an equal balance
// passes. A batch starts one sample at a time and stops at the first refusal.
//
// Prices come from GET /organisation/details. A price that is not exposed there
// (ont_bam_price, pacbio_bam_price, carrier_price today) is unknown, never 0: the
// panel says "Confirmed at start" instead of a number.
//
//   const prices = usePrices()                 // null until loaded
//   priceOf(workflow, sample, prices)          // { credits, parts: [{ label, credits }] }
//   batchPrice(workflow, samples, prices)      // { total, known, lines, perSample, withAI, ... }
//   groupPrice(mode, groupState, prices)       // multisample, cohort, carrier
//
// `prices` may also be the raw /organisation/details response: both have the same
// field names.
// ----------------------------------------------------------------------

export const PRICE_FIELDS = Object.freeze([
  'vcf_price',
  'targeted_whole_exome_fastq_price',
  'targeted_clinical_exome_fastq_price',
  'whole_genome_fastq_price',
  'ont_bam_price',
  'pacbio_bam_price',
  'cnv_price',
  'ai_price',
  'carrier_price',
  'cnv_cohort_price',
  'rna_fastq_price',
  'rna_targeted_fastq_price',
  'tumour_normal_whole_exome_fastq_price',
  'tumour_normal_clinical_exome_fastq_price',
  'tumour_normal_whole_genome_fastq_price',
])

// A price that is missing, empty or not a number is unknown (null), never 0.
const creditsOf = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Prices and balance from an /organisation/details response, or null without one. */
export const pricesFrom = (org) => {
  if (!org || typeof org !== 'object') return null
  const out = { balance: creditsOf(org.balance) }
  PRICE_FIELDS.forEach((field) => {
    out[field] = creditsOf(org[field])
  })
  return out
}

/**
 * The organisation's prices and balance, from the /organisation/details query the
 * rest of the app shares ('organisation-details'). Null until it has loaded.
 */
export const usePrices = () => {
  const { data } = useOrganisationDetails() || {}
  return useMemo(() => pricesFrom(data), [data])
}

/** The balance in credits, or null when unknown. */
export const balanceOf = (prices) => (prices ? creditsOf(prices.balance) : null)

const priceField = (prices, field) => (prices ? creditsOf(prices[field]) : null)

// ----------------------------------------------------------------------
// Labels

export const PRICE_LABEL = Object.freeze({
  vcf: 'VCF',
  wes: 'Whole exome',
  ces: 'Clinical exome',
  wgs: 'Whole genome',
  fastq: 'FASTQ',
  ont: 'ONT BAM',
  pacbio: 'PacBio BAM',
  cnv: 'CNV',
  ai: 'AI',
  carrier: 'Carrier analysis',
  rna: 'Tumour RNA',
  rnaTargeted: 'Tumour RNA, targeted',
})

const FASTQ_FIELD = {
  wes: 'targeted_whole_exome_fastq_price',
  ces: 'targeted_clinical_exome_fastq_price',
  wgs: 'whole_genome_fastq_price',
}

const TUMOUR_NORMAL_FIELD = {
  wes: 'tumour_normal_whole_exome_fastq_price',
  ces: 'tumour_normal_clinical_exome_fastq_price',
  wgs: 'tumour_normal_whole_genome_fastq_price',
}

// Library preparations charged the targeted RNA price (the server's
// rna_library.RNA_TARGETED_PRICE_PREPS).
const RNA_TARGETED_PREPS = ['exome_capture', 'targeted_capture']

// ----------------------------------------------------------------------
// Which options a sample's start sends

const withDefaults = (workflow) => ({ ...WORKFLOW_DEFAULTS, ...(workflow || {}) })

/**
 * True when this sample's start runs AI (and so pays ai_price): the panel's
 * per-sample rule (model/samples effectiveAI), which is what the start sends.
 */
export const runsAI = (workflow, sample) => {
  const wf = withDefaults(workflow)
  if (GROUP_ANALYSES.includes(wf.analysis)) return false
  return Boolean(effectiveAI(wf, sample))
}

// CNV as the start sends it: germline FASTQ exome with the batch's CNV on, unless
// the sample opts out; PacBio when the sample's CNV runs. ONT CNV is free.
const runsPaidCnv = (wf, sample, type) => {
  if (type === 'FASTQ') return Boolean(wf.cnv) && cnvAvailable(wf) && !sample?.excludeCNV
  if (type === 'PACBIO_BAM') return effectiveLongRead(wf, sample).cnv
  return false
}

const baseOf = (wf, type, sample) => {
  if (type === 'VCF') return { key: 'vcf', field: 'vcf_price' }
  if (type === 'ONT_BAM') return { key: 'ont', field: 'ont_bam_price' }
  if (type === 'PACBIO_BAM') return { key: 'pacbio', field: 'pacbio_bam_price' }
  if (type === 'FASTQ') {
    if (isTumourRna(wf)) {
      return RNA_TARGETED_PREPS.includes(libraryOf(sample).libraryPrep)
        ? { key: 'rnaTargeted', field: 'rna_targeted_fastq_price' }
        : { key: 'rna', field: 'rna_fastq_price' }
    }
    // No sequencing type yet: the server would refuse the start, so no price.
    if (!FASTQ_FIELD[wf.seq]) return { key: 'fastq', field: null }
    const tumourNormal = wf.analysis === 'somatic' && wf.mode === 'tn'
    return { key: wf.seq, field: (tumourNormal ? TUMOUR_NORMAL_FIELD : FASTQ_FIELD)[wf.seq] }
  }
  return null
}

// ----------------------------------------------------------------------
// One sample

/**
 * Credits one sample's start costs, with its parts in the order the server adds
 * them. `credits` is null when a needed price is unknown (or the workflow cannot
 * price it yet); each part's credits is null when its own price is unknown.
 * @returns {{ credits: number | null, parts: Array<{ key, label, credits: number | null }> }}
 */
export const priceOf = (workflow, sample, prices) => {
  const wf = withDefaults(workflow)
  if (GROUP_ANALYSES.includes(wf.analysis)) return { credits: null, parts: [] }
  const type = annotationTypeOf(wf, sample)
  const base = baseOf(wf, type, sample)
  if (!base) return { credits: null, parts: [] }
  const part = (key, field) => ({ key, label: PRICE_LABEL[key], credits: field ? priceField(prices, field) : null })
  const parts = [part(base.key, base.field)]
  if (runsPaidCnv(wf, sample, type)) parts.push(part('cnv', 'cnv_price'))
  if (runsAI(wf, sample)) parts.push(part('ai', 'ai_price'))
  const known = parts.every((p) => p.credits !== null)
  return { credits: known ? parts.reduce((sum, p) => sum + p.credits, 0) : null, parts }
}

const uidOf = (sample, index) => sample?.uid ?? sample?.key ?? index

/**
 * How many samples start, in order, before the first one the balance cannot cover
 * (the server refuses a price above the balance, and a batch stops there). Null
 * when the balance or a price on the way is unknown.
 */
export const balanceCovers = (creditsList, balance) => {
  const left = creditsOf(balance)
  if (left === null) return null
  let remaining = left
  for (let i = 0; i < creditsList.length; i += 1) {
    const credits = creditsList[i]
    if (credits === null || credits === undefined) return null
    if (credits > remaining) return i
    remaining -= credits
  }
  return creditsList.length
}

/**
 * The estimate for a batch of samples (the ones that will start, in start order).
 *   total      sum of the known lines
 *   known      every line's price is known (the total is the whole estimate)
 *   lines      [{ key, label, count, unit, credits }] base first, then CNV, then AI;
 *              unit and credits are null when that price is unknown
 *   perSample  Map(uid -> priceOf)
 *   withAI     how many samples run AI
 *   balance    the balance, or null
 *   short      credits missing for the whole batch (0 when covered), null if unknown
 *   covers     how many samples start before the balance runs out, null if unknown
 */
export const batchPrice = (workflow, samples, prices) => {
  const list = Array.isArray(samples) ? samples : []
  const priced = list.map((sample) => priceOf(workflow, sample, prices))
  const perSample = new Map(list.map((sample, index) => [uidOf(sample, index), priced[index]]))
  // One line per part, in the order parts first appear (base, CNV, AI).
  const byKey = new Map()
  priced.forEach(({ parts }) =>
    parts.forEach((p) => {
      const line = byKey.get(p.key) || { key: p.key, label: p.label, count: 0, unit: p.credits }
      line.count += 1
      if (p.credits === null) line.unit = null
      byKey.set(p.key, line)
    }),
  )
  const lines = [...byKey.values()].map((line) => ({
    ...line,
    credits: line.unit === null ? null : line.unit * line.count,
  }))
  // A sample the workflow cannot price at all (no parts) leaves the estimate open.
  const known = priced.every((p) => p.parts.length > 0) && lines.every((line) => line.credits !== null)
  const total = lines.reduce((sum, line) => sum + (line.credits || 0), 0)
  const balance = balanceOf(prices)
  return {
    total,
    known,
    lines,
    perSample,
    withAI: byKey.get('ai')?.count || 0,
    balance,
    short: known && balance !== null ? Math.max(total - balance, 0) : null,
    covers: known
      ? balanceCovers(
          priced.map((p) => p.credits),
          balance,
        )
      : null,
  }
}

/**
 * Credits per sample for the batch's options, as the workflow column says it: the
 * start without AI and what AI adds. Null parts when unknown or not applicable.
 * @returns {{ credits: number | null, ai: number | null }}
 */
export const workflowUnitPrice = (workflow, prices) => {
  const wf = withDefaults(workflow)
  if (GROUP_ANALYSES.includes(wf.analysis)) return { credits: null, ai: null }
  const fileType = { fastq: 'FASTQ', vcf: 'VCF', ont: 'ONT_BAM', pacbio: 'PACBIO_BAM' }[wf.input]
  const plain = priceOf(wf, { fileType, ai: false, excludeAI: true }, prices).credits
  const ai = wf.analysis === 'germline' && aiAvailable(wf, { fileType }) ? priceField(prices, 'ai_price') : null
  return { credits: plain, ai }
}

// ----------------------------------------------------------------------
// Group analyses

const arrayOf = (value) => (Array.isArray(value) ? value : [])

/**
 * The estimate for a group run. mode: 'multisample' | 'cohort' | 'carrier'.
 * groupState is the panel's group state (groups/model.js); for carrier, a list of
 * carrier groups works too. Multisample and CNV cohort are free.
 * @returns {{ total, known, free, lines: [{ key, label, count, unit, credits }], balance, short, covers }}
 */
export const groupPrice = (mode, groupState, prices) => {
  const balance = balanceOf(prices)
  if (mode === 'multisample' || mode === 'cohort') {
    return { total: 0, known: true, free: true, lines: [], balance, short: 0, covers: null }
  }
  if (mode !== 'carrier') {
    return { total: 0, known: false, free: false, lines: [], balance, short: null, covers: null }
  }
  const groups = Array.isArray(groupState) ? groupState : arrayOf(groupState?.carrier?.groups)
  const count = groups.filter((g) => arrayOf(g?.members).some((m) => m && m.fileId)).length
  const unit = priceField(prices, 'carrier_price')
  const lines = count
    ? [{ key: 'carrier', label: PRICE_LABEL.carrier, count, unit, credits: unit === null ? null : unit * count }]
    : []
  const known = unit !== null || count === 0
  const total = unit === null ? 0 : unit * count
  return {
    total,
    known,
    free: false,
    lines,
    balance,
    short: known && balance !== null ? Math.max(total - balance, 0) : null,
    covers: known ? balanceCovers(new Array(count).fill(unit), balance) : null,
  }
}

// ----------------------------------------------------------------------
// Words

/** "1,713": whole credits with thousands separators, '' when not a number. */
export const formatCredits = (value) =>
  Number.isFinite(value) ? Math.round(value).toLocaleString('en-US', { maximumFractionDigits: 0 }) : ''

/** "1 credit", "14 credits", '' when not a number. */
export const creditsText = (value) =>
  Number.isFinite(value) ? `${formatCredits(value)} ${Math.round(value) === 1 ? 'credit' : 'credits'}` : ''

export const UNKNOWN_PRICE_TEXT = 'Confirmed at start'

/** "Whole exome 1 + CNV 6 + AI 7"; an unknown part reads "confirmed at start". */
export const partsText = (parts) =>
  arrayOf(parts)
    .map((p) => `${p.label} ${p.credits === null ? UNKNOWN_PRICE_TEXT.toLowerCase() : formatCredits(p.credits)}`)
    .join(' + ')
